// Product-triggered auto gift (include whitelist + exclude blacklist).
// Independent of Free Gifts (subtotal thresholds) and cart progress bar unlocks.
// Configuration is injected via window.ProductGiftConfig (see layout/theme.liquid).
//
// Priority:
// 1. cart.total (excluding product-auto + Free Gifts lines) must meet that rule's threshold.
// 2. Include enabled + any include-trigger in cart → include gift only.
// 3. Else, exclude enabled + any non-gift product NOT in the exclude list → exclude gift only.
// 4. Else → remove both managed gifts.
// Upgrade: when step 2 or 3 earned a gift and the cart holds at least minSmall small units,
// OR at least minLarge large units, OR at least minTotal small + large units together
// (upgrade collections), that gift is swapped for the upgrade bag.
// A minimum of 0 disables that condition.
// Free Gifts (FreeGiftConfig) never count as include/exclude triggers.
// Disabled rules never add their gift and always remove it if present. Never both gifts at once.
(function () {
  'use strict';

  const DEBOUNCE_MS = 150;
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/;
  const BUSY_CLASS = 'product-gift-busy';
  const CHECKOUT_SELECTOR = '[name="checkout"]';
  const FLUSH_FLAG = '_productGiftFlushed';
  const COLLECTION_CACHE_TTL_MS = 10 * 60 * 1000;
  const COLLECTION_PAGE_SIZE = 250;

  const log = (...args) => {
    if (window.ProductGiftDebug) console.debug('[ProductGift]', ...args);
  };

  const config = window.ProductGiftConfig || {};
  const includeRule = config.includeRule || {};
  const excludeRule = config.excludeRule || {};
  const upgradeRule = config.upgradeRule || {};

  const includeVariantId = includeRule.variantId || null;
  const excludeVariantId = excludeRule.variantId || null;
  const upgradeVariantId = upgradeRule.variantId || null;
  const includeThresholdCents = Number(includeRule.thresholdCents) || 0;
  const excludeThresholdCents = Number(excludeRule.thresholdCents) || 0;

  if (!includeVariantId && !excludeVariantId && !upgradeVariantId) {
    log('No gift products configured — exiting.');
    return;
  }

  const includeActive =
    !!includeRule.enabled &&
    !!includeVariantId &&
    Array.isArray(includeRule.triggerProductIds) &&
    includeRule.triggerProductIds.length > 0;

  // Empty exclude list = every non-gift product qualifies for the exclude gift.
  const excludeActive = !!excludeRule.enabled && !!excludeVariantId;

  const upgradeMinSmall = Math.max(0, Number(upgradeRule.minSmall) || 0);
  const upgradeMinLarge = Math.max(0, Number(upgradeRule.minLarge) || 0);
  const upgradeMinTotal = Math.max(0, Number(upgradeRule.minTotal) || 0);
  const upgradeSmallCollection = {
    handle: upgradeRule.smallCollection || null,
    count: Number(upgradeRule.smallCollectionCount) || 0,
  };
  const upgradeLargeCollection = {
    handle: upgradeRule.largeCollection || null,
    count: Number(upgradeRule.largeCollectionCount) || 0,
  };
  // All minimums at 0 = no condition to meet — treat as inactive.
  const upgradeActive =
    !!upgradeRule.enabled &&
    !!upgradeVariantId &&
    (upgradeMinSmall > 0 || upgradeMinLarge > 0 || upgradeMinTotal > 0);

  // Product Auto Gift lines — managed by this script.
  const managedVariantIds = new Set();
  if (includeVariantId) managedVariantIds.add(includeVariantId);
  if (excludeVariantId) managedVariantIds.add(excludeVariantId);
  if (upgradeVariantId) managedVariantIds.add(upgradeVariantId);

  // Free Gifts (threshold gifts from FreeGiftConfig) must not count as paid
  // trigger products for include/exclude membership or threshold totals.
  const freeGiftVariantIds = new Set(
    ((window.FreeGiftConfig && window.FreeGiftConfig.gifts) || [])
      .filter((g) => g && g.variantId)
      .map((g) => g.variantId),
  );

  const ignoredVariantIds = new Set([...managedVariantIds, ...freeGiftVariantIds]);

  function isIgnoredGiftLine(item) {
    return ignoredVariantIds.has(item.variant_id);
  }

  log('Rules:', {
    includeThresholdCents,
    excludeThresholdCents,
    includeActive,
    excludeActive,
    upgradeActive,
    freeGiftVariantIds: [...freeGiftVariantIds],
    includeRule,
    excludeRule,
    upgradeRule,
  });

  let inFlight = false;
  let pendingReconcile = false;
  let debounceTimer = null;

  const style = document.createElement('style');
  style.textContent = `
    body.${BUSY_CLASS} ${CHECKOUT_SELECTOR} {
      pointer-events: none !important;
      opacity: 0.5 !important;
      cursor: wait !important;
    }
  `;
  (document.head || document.documentElement).appendChild(style);

  function setBusy(busy) {
    document.body && document.body.classList.toggle(BUSY_CLASS, busy);
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((btn) => {
      btn.disabled = busy;
      if (busy) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
  }

  function scheduleReconcile(fromMutation) {
    if (fromMutation) setBusy(true);
    if (inFlight) {
      pendingReconcile = true;
      return;
    }
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runReconcile, DEBOUNCE_MS);
  }

  async function runReconcile() {
    debounceTimer = null;
    await reconcile();
    if (debounceTimer === null && !inFlight && !pendingReconcile) setBusy(false);
  }

  async function flushReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    while (inFlight) {
      await new Promise((r) => setTimeout(r, 20));
    }
    pendingReconcile = false;
    await reconcile();
    while (pendingReconcile) {
      pendingReconcile = false;
      await reconcile();
    }
  }

  function cartHasAny(cart, productIds) {
    return cart.items.some(
      (i) => !isIgnoredGiftLine(i) && productIds.includes(i.product_id),
    );
  }

  // True when cart has any paid (non-gift) line whose product is NOT in the exclude list.
  function cartHasProductOutsideExcludeList(cart) {
    const excluded = excludeRule.triggerProductIds || [];
    return cart.items.some((i) => {
      if (isIgnoredGiftLine(i)) return false;
      return !excluded.includes(i.product_id);
    });
  }

  // Paid (non-gift) units in the cart whose product is in productIds (a Set).
  function countUnits(cart, productIds) {
    return cart.items
      .filter((i) => !isIgnoredGiftLine(i) && productIds.has(i.product_id))
      .reduce((n, i) => n + i.quantity, 0);
  }

  // Collection product IDs via /products.json (no 50-product cap of Liquid settings).
  // Cached per tab for COLLECTION_CACHE_TTL_MS; the key includes products_count so
  // adding/removing products invalidates it on the next page load.
  const collectionIdsPromises = {};

  function loadCollectionIds(collection) {
    if (!collection.handle) return Promise.resolve(new Set());
    const key = `productGift:${collection.handle}:${collection.count}`;
    if (!collectionIdsPromises[key]) {
      collectionIdsPromises[key] = fetchCollectionIds(collection.handle, key).then((ids) => {
        if (!ids) {
          // Failed fetch: let the next reconcile retry instead of keeping an empty set.
          delete collectionIdsPromises[key];
          return new Set();
        }
        return ids;
      });
    }
    return collectionIdsPromises[key];
  }

  async function fetchCollectionIds(handle, key) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.at < COLLECTION_CACHE_TTL_MS) return new Set(cached.ids);
    } catch (e) {
      // sessionStorage unavailable — fall through to network.
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const ids = [];
    try {
      for (let page = 1; page <= 100; page++) {
        const url = `${root}collections/${encodeURIComponent(handle)}/products.json?limit=${COLLECTION_PAGE_SIZE}&page=${page}`;
        const res = await fetch(url, {credentials: 'same-origin'});
        if (!res.ok) {
          log('Collection fetch failed:', handle, res.status);
          return null;
        }
        const products = (await res.json()).products || [];
        products.forEach((p) => ids.push(p.id));
        if (products.length < COLLECTION_PAGE_SIZE) break;
      }
    } catch (e) {
      log('Collection fetch error:', handle, e);
      return null;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify({at: Date.now(), ids}));
    } catch (e) {
      // Quota or private mode — the in-memory promise still caches for this page.
    }
    log('Collection loaded:', handle, ids.length);
    return new Set(ids);
  }

  async function meetsUpgrade(cart) {
    if (!upgradeActive) return false;
    if (upgradeMinSmall > 0) {
      const ids = await loadCollectionIds(upgradeSmallCollection);
      if (countUnits(cart, ids) >= upgradeMinSmall) return true;
    }
    if (upgradeMinLarge > 0) {
      const ids = await loadCollectionIds(upgradeLargeCollection);
      if (countUnits(cart, ids) >= upgradeMinLarge) return true;
    }
    if (upgradeMinTotal > 0) {
      const [smallIds, largeIds] = await Promise.all([
        loadCollectionIds(upgradeSmallCollection),
        loadCollectionIds(upgradeLargeCollection),
      ]);
      // A product in both collections is counted once.
      const allIds = new Set([...smallIds, ...largeIds]);
      if (countUnits(cart, allIds) >= upgradeMinTotal) return true;
    }
    return false;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', {credentials: 'same-origin'});
    if (!res.ok) {
      log('GET /cart.js failed:', res.status);
      return null;
    }
    return res.json();
  }

  async function syncGiftLine(cart, variantId, shouldHaveGift) {
    if (!variantId) return false;
    const giftLine = cart.items.find((i) => i.variant_id === variantId);
    log({variantId, giftInCart: !!giftLine, shouldHaveGift});

    if (shouldHaveGift && !giftLine) {
      return addGift(variantId);
    }
    if (shouldHaveGift && giftLine && giftLine.quantity !== 1) {
      return changeQty(giftLine.key, 1);
    }
    if (!shouldHaveGift && giftLine) {
      return changeQty(giftLine.key, 0);
    }
    return false;
  }

  async function reconcile() {
    if (inFlight) return;
    inFlight = true;
    try {
      let cart = await fetchCart();
      if (!cart) return;
      let mutated = false;

      // cart.total excluding product-auto gifts AND Free Gifts lines so neither
      // keeps the cart over a threshold or counts as a trigger product.
      const giftLinesTotal = cart.items
        .filter((i) => ignoredVariantIds.has(i.variant_id))
        .reduce((sum, i) => sum + i.final_line_price, 0);
      const cartTotalWithoutGifts = cart.total_price - giftLinesTotal;

      const wantInclude =
        includeActive &&
        cartTotalWithoutGifts >= includeThresholdCents &&
        cartHasAny(cart, includeRule.triggerProductIds);
      const wantExclude =
        excludeActive &&
        cartTotalWithoutGifts >= excludeThresholdCents &&
        !wantInclude &&
        cartHasProductOutsideExcludeList(cart);

      let targetVariantId = null;
      if (wantInclude) targetVariantId = includeVariantId;
      else if (wantExclude) targetVariantId = excludeVariantId;
      const upgraded = !!targetVariantId && (await meetsUpgrade(cart));
      if (upgraded) targetVariantId = upgradeVariantId;

      log({
        cartTotal: cart.total_price,
        cartTotalWithoutGifts,
        includeThresholdCents,
        excludeThresholdCents,
        wantInclude,
        wantExclude,
        upgraded,
        targetVariantId,
        includeActive,
        excludeActive,
      });

      // Strip every other managed gift (even from disabled rules), then keep the target at qty 1.
      for (const variantId of managedVariantIds) {
        if (variantId === targetVariantId) continue;
        if (await syncGiftLine(cart, variantId, false)) {
          mutated = true;
          cart = (await fetchCart()) || cart;
        }
      }
      if (targetVariantId && (await syncGiftLine(cart, targetVariantId, true))) mutated = true;

      if (mutated) {
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', {detail: {}}));
      }
    } catch (e) {
      log('Reconcile error:', e);
    } finally {
      inFlight = false;
      if (pendingReconcile) {
        pendingReconcile = false;
        scheduleReconcile(true);
      }
    }
  }

  async function addGift(variantId) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: variantId, quantity: 1}),
      });
      if (!res.ok) log('addGift failed:', variantId, res.status);
      return res.ok;
    } catch (e) {
      log('addGift error:', variantId, e);
      return false;
    }
  }

  async function changeQty(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: key, quantity}),
      });
      if (!res.ok) log('changeQty failed:', res.status);
      return res.ok;
    } catch (e) {
      log('changeQty error:', e);
      return false;
    }
  }

  const reapplyObserver = new MutationObserver(() => {
    if (document.body && document.body.classList.contains(BUSY_CLASS)) {
      document.querySelectorAll(CHECKOUT_SELECTOR).forEach((btn) => {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
      });
    }
  });
  const startObserver = () => {
    if (document.body) reapplyObserver.observe(document.body, {childList: true, subtree: true});
  };
  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver, {once: true});

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleReconcile(false), {once: true});
  } else {
    scheduleReconcile(false);
  }

  document.addEventListener('cart:updated', () => scheduleReconcile(true));

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (form.dataset[FLUSH_FLAG] === '1') {
        delete form.dataset[FLUSH_FLAG];
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      setBusy(true);
      flushReconcile().finally(() => {
        setBusy(false);
        form.dataset[FLUSH_FLAG] = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter);
        } else {
          form.submit();
        }
      });
    },
    true,
  );

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const promise = originalFetch.apply(this, args);
    const url = args[0] instanceof Request ? args[0].url : String(args[0] || '');
    if (CART_MUTATION_RE.test(url)) {
      setBusy(true);
      promise
        .then((res) => {
          if (res && res.ok) scheduleReconcile(true);
          else if (debounceTimer === null && !inFlight && !pendingReconcile) setBusy(false);
        })
        .catch(() => {
          if (debounceTimer === null && !inFlight && !pendingReconcile) setBusy(false);
        });
    }
    return promise;
  };
})();
